import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RecommendationLabClient } from '../../../infrastructure/RecommendationLabClient';
import type {
  RecommendationLabPreviewRequest,
  RecommendationLabPreviewResult,
} from '../../../infrastructure/RecommendationLabClient';

export const useRecommendationLab = () => {
  const client = useMemo(() => new RecommendationLabClient(), []);
  const [lastLatencyMs, setLastLatencyMs] = useState<number | null>(null);
  const inputOptionsQuery = useQuery({
    queryKey: ['recommendation-lab', 'input-options'],
    queryFn: () => client.getInputOptions(),
  });

  const previewMutation = useMutation<RecommendationLabPreviewResult, Error, RecommendationLabPreviewRequest>({
    mutationFn: async (request) => {
      const startedAt = performance.now();
      try {
        return await client.preview(request);
      } finally {
        setLastLatencyMs(Math.round(performance.now() - startedAt));
      }
    },
  });
  const resetPreviewRef = useRef(previewMutation.reset);
  resetPreviewRef.current = previewMutation.reset;
  const clearResult = useCallback(() => {
    resetPreviewRef.current();
    setLastLatencyMs(null);
  }, []);

  return {
    preview: previewMutation.mutateAsync,
    inputOptions: inputOptionsQuery.data ?? null,
    isInputOptionsLoading: inputOptionsQuery.isPending,
    inputOptionsError: inputOptionsQuery.error?.message ?? null,
    reloadInputOptions: inputOptionsQuery.refetch,
    result: previewMutation.data ?? null,
    clearResult,
    isLoading: previewMutation.isPending,
    error: previewMutation.error?.message ?? null,
    lastLatencyMs,
  };
};
